import { Component, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MrpService } from '../../services/mrp.service';
import { LucideAngularModule, Plus, Search, Trash, Save, X, Box, Layers, AlertCircle, Check, Settings, Calendar, Package, FileText, ToggleLeft } from 'lucide-angular';
import { MrpProfile } from '../../models/mrp-profile.model';

@Component({
    selector: 'app-profiles',
    imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
    templateUrl: './profiles.component.html',
    styleUrls: ['./profiles.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilesComponent {
    readonly #mrpService = inject(MrpService);
    readonly fb = new FormBuilder();

    readonly icons = { Plus, Search, Trash, Save, X, Box, Layers, AlertCircle, Check, Settings, Calendar, Package, FileText, ToggleLeft };

    readonly profiles = this.#mrpService.profiles;
    readonly selectedProfileId = signal<string | null>(null);
    readonly searchQuery = signal<string>('');
    readonly profileToDeleteId = signal<string | null>(null);

    get mrpService() { return this.#mrpService; }

    readonly profileForm: FormGroup = this.fb.group({
        id: [''],
        name: ['', Validators.required],

        TELA_1: this.fb.group({
            HW2_CODIGO: ['', Validators.required]
        }),

        // Selects (enums)
        periodType: ['1'],
        allocationSuggestion: ['2'],
        leadTime: ['1'],
        safetyStock: ['1'],
        orderPoint: ['1'],
        blockedLot: ['2'],
        consignedIn: ['2'],
        consignedOut: ['1'],
        rejectedQuality: ['2'],
        consolidateProductionOrder: ['3'],
        consolidatePurchaseRequest: ['3'],
        firmHorizon: ['2'],
        productionOrderNumber: ['2'],
        productionOrderType: ['1'],
        purchaseRequestNumber: ['2'],

        // Numérico
        numberOfPeriods: ['1'],

        // Datas
        demandEndDate: ['30'],

        // Booleans
        demandsProcessed: [true],
        eventLog: [false],
        lGeraDoc: [true],
        lRastreiaEntradas: [false],
        lDocAlcada: [false],
        doTransfersMrp: [false],

        // Armazém
        armazemPad: ['2'],
        armazemDe: [''],
        armazemAte: [''],
        warehouses: [''],

        // Setup
        setupCode: [''],
        setupDescription: [''],

        // Fornecedor
        supplierCode: [''],
        supplierStore: [''],

        // Nested Objects
        demandCodes: this.fb.group({
            LISTA: ['']
        }),
        demandType: this.fb.group({
            PEDIDO_VENDA: [true],
            PREV_VENDAS: [true],
            PLANO_MESTRE: [true],
            EMP_PROJETO: [false],
            MANUAL: [true]
        }),
        documents: this.fb.group({
            LISTA: ['']
        }),
        documentType: this.fb.group({
            PREVISTOS: ['1'],
            SACRAMENTADOS: [true],
            SUSPENSOS: [true]
        }),
        productGroups: this.fb.group({
            LISTA: ['']
        }),
        products: this.fb.group({
            LISTA: ['']
        }),
        productTypes: this.fb.group({
            LISTA: ['']
        }),
    });

    readonly filteredProfiles = computed(() => {
        const query = this.searchQuery().toLowerCase();
        const all = this.profiles();
        if (!query) {
            return all;
        } else {
            return all.filter(p =>
                (p.name && p.name.toLowerCase().includes(query)) ||
                (p.TELA_1 && p.TELA_1.HW2_CODIGO && p.TELA_1.HW2_CODIGO.toLowerCase().includes(query))
            );
        }
    });

    onSearch(event: Event) {
        this.searchQuery.set((event.target as HTMLInputElement).value);
    }

    selectProfile(profile: MrpProfile) {
        this.selectedProfileId.set(profile.id);
        this.profileForm.patchValue(profile);
        this.profileForm.markAsPristine();
    }

    newProfile() {
        this.selectedProfileId.set('NEW');
        this.profileForm.reset({
            id: crypto.randomUUID(),
            name: '',
            TELA_1: { HW2_CODIGO: '' },
            periodType: '1',
            allocationSuggestion: '2',
            leadTime: '1',
            safetyStock: '1',
            orderPoint: '1',
            blockedLot: '2',
            consignedIn: '2',
            consignedOut: '1',
            rejectedQuality: '2',
            consolidateProductionOrder: '3',
            consolidatePurchaseRequest: '3',
            firmHorizon: '2',
            productionOrderNumber: '2',
            productionOrderType: '1',
            purchaseRequestNumber: '2',
            numberOfPeriods: '1',
            demandEndDate: '30',
            demandsProcessed: true,
            eventLog: false,
            lGeraDoc: true,
            lRastreiaEntradas: false,
            lDocAlcada: false,
            doTransfersMrp: false,
            armazemPad: '2',
            armazemDe: '',
            armazemAte: '',
            warehouses: '',
            setupCode: '',
            setupDescription: '',
            supplierCode: '',
            supplierStore: '',
            demandType: {
                PEDIDO_VENDA: true,
                PREV_VENDAS: true,
                PLANO_MESTRE: true,
                EMP_PROJETO: false,
                MANUAL: true
            },
            documentType: {
                PREVISTOS: '1',
                SACRAMENTADOS: true,
                SUSPENSOS: true
            }
        });
    }

    saveProfile() {
        if (this.profileForm.invalid) return;

        const formVal = this.profileForm.value;

        if (this.selectedProfileId() === 'NEW') {
            this.#mrpService.createProfile(formVal);
        } else {
            this.#mrpService.updateProfile(formVal);
        }

        this.selectedProfileId.set(null);
    }

    confirmDelete(id: string, event: Event) {
        event.stopPropagation();
        this.profileToDeleteId.set(id);
    }

    cancelDelete() {
        this.profileToDeleteId.set(null);
    }

    deleteProfile() {
        const id = this.profileToDeleteId();
        if (id) {
            this.#mrpService.deleteProfile(id);
            if (this.selectedProfileId() === id) {
                this.selectedProfileId.set(null);
            }
        }
        this.profileToDeleteId.set(null);
    }

    toggleFormControl(controlName: string) {
        const control = this.profileForm.get(controlName);
        if (control) {
            control.setValue(!control.value);
            control.markAsDirty();
        }
    }
}
